import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useActiveRole } from "../../context/ActiveRoleContext";
import profileIcon from "../../assets/profile_icon_default.png";
import { ROLE_NAMES } from "../../constants/userRoles";

export default function TopHeader({ title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // El badge muestra el rol ACTIVO, no el primero que mando el backend: `user.roleId` es el
  // ordinal del primer UserRole que encontro y con varios roles salia uno al azar.
  const { activeRoleId, availableRoles, clearSelection } = useActiveRole();

  const [expand, setExpand] = useState(false);
  const dropdownRef = useRef(null);

  // Cerrar al clickear afuera (mismo patron que dataUser.jsx del CMS).
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setExpand(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const roleName =
    activeRoleId !== null && ROLE_NAMES[activeRoleId]
      ? ROLE_NAMES[activeRoleId]
      : "Usuario";

  const fullName =
    user?.name || user?.lastName
      ? `${user?.name || ""} ${user?.lastName || ""}`.trim()
      : "Usuario";

  const canSwitchRole = Array.isArray(availableRoles) && availableRoles.length > 1;

  const handleSwitchRole = () => {
    setExpand(false);
    // Solo se suelta el rol activo (no la sesion): el guard de rutas manda al selector.
    clearSelection();
    navigate("/select-role", { replace: true });
  };

  const handleLogout = () => {
    setExpand(false);
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <header className="top-header">
      <style>{`
        .user-menu-wrapper { position: relative; }

        .user-menu-trigger {
          padding: 0;
          border: none;
          background: none;
          cursor: pointer;
          border-radius: 50%;
          transition: box-shadow 0.2s ease;
        }
        .user-menu-trigger:hover { box-shadow: 0 0 0 3px rgba(234, 192, 36, 0.35); }
        .user-menu-trigger:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(234, 192, 36, 0.6);
        }

        .user-menu-dropdown {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          min-width: 230px;
          background: #FFFFFF;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
          padding: 6px;
          z-index: 1000;
        }

        .user-menu-item {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border: none;
          background: none;
          border-radius: 8px;
          cursor: pointer;
          font-family: inherit;
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          text-align: left;
        }
        .user-menu-item:hover { background: #F9FAFB; }
        .user-menu-item.is-danger { color: #DC2626; }
        .user-menu-item.is-danger:hover { background: #FEF2F2; }

        .user-menu-item-subtitle {
          display: block;
          font-size: 11px;
          font-weight: 500;
          color: #9CA3AF;
          margin-top: 2px;
        }

        .user-menu-divider { height: 1px; background: #F3F4F6; margin: 4px 6px; }
      `}</style>

      <h1 className="page-title">{title}</h1>

      <div className="user-profile" style={{ gap: '16px', alignItems: 'center' }}>

        {/* 🔥 CAMBIO: alignItems a 'center' para que el nombre y el rol estén centrados entre sí */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>

          {/* Nombre con tamaño normal pero en negrita */}
          <span className="user-name" style={{ fontSize: '18px', fontWeight: 'bold', color: '#374151' }}>
            {fullName}
          </span>

          {/* 🔥 CAMBIOS EN EL ROL:
              - fontSize más grande (15px).
              - padding horizontal mucho mayor (24px) para que sea alargado.
              - textAlign: 'center' por si acaso.
          */}
          <span className="user-badge" style={{
            fontSize: '15px',
            padding: '4px 24px',
            borderRadius: '99px',
            fontWeight: '600',
            textAlign: 'center'
          }}>
            {roleName}
          </span>

        </div>

        {/* El avatar abre el menú: "Cambiar rol" (solo con más de un rol) y "Cerrar sesión" */}
        <div className="user-menu-wrapper" ref={dropdownRef}>
          <button
            type="button"
            className="user-menu-trigger"
            onClick={() => setExpand((prev) => !prev)}
            aria-haspopup="menu"
            aria-expanded={expand}
            aria-label="Menú de usuario"
          >
            <div className="user-avatar" style={{ width: '55px', height: '55px' }}>
              <img src={profileIcon} alt="Foto de perfil" />
            </div>
          </button>

          {expand && (
            <div className="user-menu-dropdown" role="menu">
              {canSwitchRole && (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    className="user-menu-item"
                    onClick={handleSwitchRole}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="17 1 21 5 17 9" />
                      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                      <polyline points="7 23 3 19 7 15" />
                      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                    </svg>
                    <span>
                      Cambiar rol
                      <span className="user-menu-item-subtitle">Actual: {roleName}</span>
                    </span>
                  </button>
                  <div className="user-menu-divider" />
                </>
              )}

              <button
                type="button"
                role="menuitem"
                className="user-menu-item is-danger"
                onClick={handleLogout}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span>Cerrar sesión</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
